import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import i18next from 'i18next';

import View from './views/View';
import { ARSize } from './views/ARSize';
import { Guide } from './views/Guide';
import { HeaderBar } from './views/HeaderBar';
import { KompanPlannerThemeProvider } from './layout/UiTheme';
import { KompanSettingsManager } from './managers/KompanSettingsManager';
import { LoadingTile } from './views/LoadingView';
import { MusicManager } from './managers/MusicManager';
import { ProjectSettingsModule } from './module/ProjectSettingsModule';
import { RoomResources } from './utils/RoomResources';
import { SceneManager } from './managers/SceneManager';
import { SessionManager } from './managers/SessionManager';
import { ShareActions } from './actions/ShareActions';
import { StartupActions } from './actions/StartupActions';
import { ViewManager, ViewLayer } from './managers/ViewManager';
import { ViewRoot } from './views/ViewRoot';
import { WalkthroughTool } from './tool/Tool';

import { BaseApp } from '@egr/xbox/base-app/App';
import { AppContext } from '@egr/xbox/base-app/AppContext';
import { FullscreenContainer } from '@egr/xbox/threeD/subcomponents/FullscreenContainer';
import { ProgressManager } from '@egr/xbox/progress-manager/ProgressManager';
import { setupSentry } from '@egr/xbox/utils/Error';

import { ArticleManager } from '@egr/wcf/modules/cf';
import { Default } from '@egr/wcf/modules/pl/tool/Default';
import { RoomManager, VisibilityMode } from '@egr/room/modules/RoomManager';
import { ajax } from '@egr/wcf/modules/utils/async';
import { getFilePath, getUrlParamValue } from '@egr/wcf/modules/utils/string';

setupSentry();

interface Config {
    manufacturerSeries: Array<string>;
    layerControl: Array<string>;
}

export interface DefaultAppProps {
    app: App;
}

class App extends BaseApp {
    public readonly roomManager: RoomManager;
    public override readonly sessionManager: SessionManager = new SessionManager();
    public override readonly sceneManager: SceneManager = new SceneManager(false);
    public readonly kompanSettingsManager: KompanSettingsManager = new KompanSettingsManager();
    public readonly viewManager: ViewManager = new ViewManager();
    public readonly musicManager = new MusicManager(this);

    public shareActions: ShareActions;
    public readonly startupActions: StartupActions = new StartupActions(this);

    public override componentDidMount(): void {
        super.componentDidMount();
        void this.initializeManagers();
    }

    public override render(): JSX.Element {
        return (
            <AppContext.Provider value={this}>
                {/* <I18nextProvider i18n={i18n}> */}
                    <KompanPlannerThemeProvider>
                        <CssBaseline>
                            <ViewRoot app={this}>
                                <LoadingTile app={this}/>
                                <View
                                    visible={true}
                                    color="default"
                                    keepMounted={true}
                                >
                                    <FullscreenContainer visible={true} viewerID={this.viewerID}/>
                                    <HeaderBar app={this} />
                                </View>
                                <Guide app={this} />
                                <ARSize app={this} />
                            </ViewRoot>
                        </CssBaseline>
                    </KompanPlannerThemeProvider>
                {/* </I18nextProvider> */}
            </AppContext.Provider>
        );
    }

    public handleNetworkError(retryCallback: VoidFunction): void {
        this.messageManager.showModalMessage(
            i18next.t('Connection interrupted'),
            i18next.t('Connect'),
            async (): Promise<boolean> => {
                retryCallback();
                return true;
            }
        );
    }

    protected handleUnhandledRejection(error: Error): void {
        // nothing to do at the moment
    }

    private async initializeRoom(articleManager: ArticleManager): Promise<void> {
        const layers: Array<string> = [];
        let config: Config | null = null;
        try {
            config = await ajax<Config>('GET', 'config/config.json', null, { responseType: 'json' });
        } catch (error) {
            /**/
        }

        let manufacturerSeries: Array<string> = [];
        if (config != null && Array.isArray(config.manufacturerSeries)) {
            manufacturerSeries = config.manufacturerSeries;
            if (Array.isArray(config.layerControl)) {
                for (const series of config.manufacturerSeries) {
                    for (const layerControl of config.layerControl) {
                        layers.push(layerControl.replace(/\{\{manufacturerSeries\}\}/g, series));
                    }
                }
            }
        }

        const rootPath: string = getFilePath(window.location.pathname);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.roomManager as any) = new RoomManager(
            this.coreApp,
            rootPath + 'w-cf-room/data/',
            'EN',
            '001',
            true,
            false,
            articleManager,
            false,
            layers,
            null,
            true
        );

        this.roomManager.resources = new RoomResources();

        this.roomManager.setVisibilityMode(VisibilityMode.Show3d);
        this.roomManager.setProjectManufacturer('KOM');
        this.roomManager.setManufacturerSeries(manufacturerSeries);
        this.roomManager.setSZ_standards(['EN', 'ASTM', 'CSA', 'AS']);
        this.roomManager.setSZ_standard('EN');
        this.roomManager.eventAnimationEnded.addListener(this.sceneManager.onAnimationEnded);
        this.roomManager.eventAnimationStarted.addListener(this.sceneManager.onAnimationStarted);
        this.roomManager.eventAnimationPaused.addListener(this.sceneManager.onAnimationPaused);

    }

    private async initializeManagers(): Promise<void> {
        this.coreApp.appCallbacks.beginAsyncOperation = ProgressManager.startProgress;
        this.coreApp.appCallbacks.endAsyncOperation = ProgressManager.finishProgress;
        this.sessionManager.initialize(this);
        this.kompanSettingsManager.initialize(this);
        this.viewManager.initialize(this);
        await this.viewManager.updateKeyboardLayout();
        this.sceneManager.initialize(this);

        const articleManager: ArticleManager = await this.sceneManager.getArticleManager();

        if (this.coreApp.tools.defaultTool == null) {
            this.coreApp.tools.defaultTool = new WalkthroughTool(this);
        }

        const defaultTool: Default = this.coreApp.tools.defaultTool as Default;

        defaultTool.moveXZEnabled = false;
        defaultTool.moveYEnabled = false;
        defaultTool.rotateXEnabled = false;
        defaultTool.rotateYEnabled = false;
        defaultTool.rotateZEnabled = false;
        defaultTool.deselectionEnabled = false;
        defaultTool.showMainElementSelection = false;
        defaultTool.showElementInteractors = false;
        defaultTool.showMainElementSelection = false;
        defaultTool.showSubElementSelection = false;

        this.coreApp.tools.startDefaultTool();

        this.coreApp.registerModule(new ProjectSettingsModule(this));

        this.coreApp.viewer.container.addEventListener('dragenter', (event) => event.preventDefault());
        this.coreApp.viewer.container.addEventListener('dragover', (event) => event.preventDefault());
        this.coreApp.viewer.container.addEventListener('drop', this.startupActions.loadFileFromDropEvent);

        this.dialogManager.initialize(this);
        this.shareActions = new ShareActions(this);

        await this.initializeRoom(articleManager);

        this.viewManager.showView(ViewLayer.Article);

        const pboxUrl: string | null = getUrlParamValue(window.location.href, 'pbox');
        const pecUrl: string | null = getUrlParamValue(window.location.href, 'pec');

        if (pboxUrl != null) {
            await this.startupActions.importPBOX(pboxUrl);
        } else if (pecUrl != null) {
            await this.startupActions.importPEC(pecUrl);
        }
    }
}

export default App;